import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Calendar as CalendarIcon, 
  Users, 
  Clock, 
  MapPin, 
  Plus,
  Search,
  Filter,
  UserPlus,
  Eye,
  Edit,
  Phone,
  Mail,
  Award,
  User,
  CreditCard,
  QrCode,
  Building2
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { agentsAPI, sitesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Planning = () => {
  const [agents, setAgents] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [sites, setSites] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('site-planning');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isAddShiftDialogOpen, setIsAddShiftDialogOpen] = useState(false);
  const [selectedSiteForPlanning, setSelectedSiteForPlanning] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    badge: '',
    phone: '',
    email: '',
    speciality: 'Surveillance',
    level: 'junior',
    hireDate: new Date().toISOString().split('T')[0],
    assignedSiteId: ''
  });
  const [shiftFormData, setShiftFormData] = useState({
    resourceType: 'agent',
    agentId: '',
    subcontractorId: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '08:00',
    breakStart: '12:00',
    breakEnd: '13:00',
    endTime: '17:00',
    shiftType: 'day'
  });
  const { toast } = useToast();

  const handleShiftInputChange = (e) => {
    setShiftFormData({
      ...shiftFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddShift = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Calculate total hours
      const start = new Date(`2000-01-01T${shiftFormData.startTime}`);
      const end = new Date(`2000-01-01T${shiftFormData.endTime}`);
      const breakStart = new Date(`2000-01-01T${shiftFormData.breakStart}`);
      const breakEnd = new Date(`2000-01-01T${shiftFormData.breakEnd}`);
      
      const totalMs = end - start - (breakEnd - breakStart);
      const totalHours = (totalMs / (1000 * 60 * 60)).toFixed(1);

      // Préparer les données pour l'API
      const shiftData = {
        resourceType: shiftFormData.resourceType,
        siteId: selectedSiteForPlanning,
        date: shiftFormData.date,
        startTime: shiftFormData.startTime,
        endTime: shiftFormData.endTime,
        breakStartTime: shiftFormData.breakStart,
        breakEndTime: shiftFormData.breakEnd,
        type: shiftFormData.shiftType
      };

      if (shiftFormData.resourceType === 'agent') {
        shiftData.agentId = shiftFormData.agentId;
      } else {
        shiftData.subcontractorId = shiftFormData.subcontractorId;
      }

      // Appel API pour créer la garde
      const token = localStorage.getItem('token');
      const response = await fetch(`${(window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL)}/api/shifts/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(shiftData)
      });

      if (response.ok) {
        const resourceName = shiftFormData.resourceType === 'agent' 
          ? agents.find(a => a.id === shiftFormData.agentId)?.name
          : subcontractors.find(s => s.id === shiftFormData.subcontractorId)?.name;

        toast({
          title: "Garde ajoutée",
          description: `Garde de ${totalHours}h créée pour ${resourceName}`,
        });
        
        setIsAddShiftDialogOpen(false);
        setShiftFormData({
          resourceType: 'agent',
          agentId: '',
          subcontractorId: '',
          date: new Date().toISOString().split('T')[0],
          startTime: '08:00',
          breakStart: '12:00',
          breakEnd: '13:00',
          endTime: '17:00',
          shiftType: 'day'
        });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur création garde');
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'ajouter la garde",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgents();
    loadSites();
    loadSubcontractors();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await agentsAPI.getAll();
      setAgents(response.data);
    } catch (error) {
      console.error('Erreur chargement agents:', error);
      setAgents([]);
    }
  };

  const loadSubcontractors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${(window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL)}/api/subcontractors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubcontractors(data.filter(s => s.active));
      }
    } catch (error) {
      console.error('Erreur chargement sous-traitants:', error);
      setSubcontractors([]);
    }
  };

  const loadSites = async () => {
    try {
      const response = await sitesAPI.getAll();
      setSites(response.data || []);
    } catch (error) {
      console.error('Erreur chargement sites:', error);
      setSites([]);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddAgent = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await agentsAPI.create(formData);
      setAgents([...agents, response.data]);
      setIsAddDialogOpen(false);
      setFormData({
        name: '',
        badge: '',
        phone: '',
        email: '',
        speciality: 'Surveillance',
        level: 'junior',
        hireDate: new Date().toISOString().split('T')[0]
      });
      toast({
        title: "Agent ajouté",
        description: "Le nouvel agent a été ajouté avec succès",
      });
      loadAgents();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'ajouter l'agent",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.badge.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.speciality.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || agent.status.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Actif': return 'bg-green-500';
      case 'Repos': return 'bg-blue-500';
      case 'En congé': return 'bg-orange-500';
      case 'Indisponible': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Actif': return 'default';
      case 'Repos': return 'secondary';
      case 'En congé': return 'outline';
      case 'Indisponible': return 'destructive';
      default: return 'outline';
    }
  };

  const getShiftColor = (type) => {
    switch (type) {
      case 'Jour': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Après-midi': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Nuit': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getShiftStatusColor = (status) => {
    switch (status) {
      case 'En cours': return 'text-green-600';
      case 'Programmé': return 'text-blue-600';
      case 'Terminé': return 'text-gray-600';
      case 'Annulé': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Expert': return 'text-purple-600 bg-purple-50';
      case 'Senior': return 'text-blue-600 bg-blue-50';
      case 'Junior': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const todayShifts = shifts.filter(shift => shift.date === selectedDate);
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  
  // Generate week view (7 days starting from selected date)
  const getWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const weekDates = getWeekDates(selectedDate);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Planning des Agents</h2>
          <p className="text-gray-600">Gestion des équipes et planification des services</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => setIsAddDialogOpen(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Nouvel agent
          </Button>
          <Button onClick={() => setIsAddShiftDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle garde
          </Button>
        </div>
      </div>

      {/* Add Agent Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajouter un agent de sécurité</DialogTitle>
            <DialogDescription>
              Enregistrez un nouvel agent dans votre équipe
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddAgent}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="badge">Numéro de badge *</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="badge"
                      name="badge"
                      value={formData.badge}
                      onChange={handleInputChange}
                      placeholder="HTS-001"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+33 6 12 34 56 78"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="agent@hts-investigation.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="assignedSiteId">Site assigné</Label>
                  <select
                    id="assignedSiteId"
                    name="assignedSiteId"
                    value={formData.assignedSiteId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Aucun site assigné</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="speciality">Spécialité *</Label>
                  <select
                    id="speciality"
                    name="speciality"
                    value={formData.speciality}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="Surveillance">Surveillance</option>
                    <option value="Intervention">Intervention</option>
                    <option value="Cynophile">Cynophile (Maître-chien)</option>
                    <option value="Incendie">Agent SSIAP (Incendie)</option>
                    <option value="Accueil">Accueil & Contrôle</option>
                    <option value="Rondier">Rondier</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="level">Niveau *</Label>
                  <select
                    id="level"
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="junior">Junior</option>
                    <option value="senior">Senior</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hireDate">Date d'embauche *</Label>
                <Input
                  id="hireDate"
                  name="hireDate"
                  type="date"
                  value={formData.hireDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <QrCode className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Badge d'accès</p>
                    <p>Le badge pourra être configuré avec QR Code ou NFC après création de l'agent</p>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Ajout...' : 'Ajouter l\'agent'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Shift Dialog */}
      <Dialog open={isAddShiftDialogOpen} onOpenChange={setIsAddShiftDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Ajouter une garde sur le site</DialogTitle>
            <DialogDescription>
              Assignez un agent avec ses horaires d'arrivée, repos et départ
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddShift}>
            <div className="space-y-6">
              {/* Site Selection */}
              <div className="space-y-2">
                <Label htmlFor="shiftSite">Site *</Label>
                <select
                  id="shiftSite"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedSiteForPlanning}
                  onChange={(e) => setSelectedSiteForPlanning(e.target.value)}
                  required
                >
                  <option value="">-- Selectionner un site --</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>{site.name}{site.client ? ` - ${site.client}` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Resource Type Selection */}
              <div className="space-y-2">
                <Label>Type de ressource *</Label>
                <div className="flex space-x-4">
                  <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    shiftFormData.resourceType === 'agent' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="resourceType"
                      value="agent"
                      checked={shiftFormData.resourceType === 'agent'}
                      onChange={(e) => setShiftFormData({
                        ...shiftFormData,
                        resourceType: e.target.value,
                        agentId: '',
                        subcontractorId: ''
                      })}
                      className="sr-only"
                    />
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    <span className="font-medium">Agent interne</span>
                  </label>
                  <label className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    shiftFormData.resourceType === 'subcontractor' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="resourceType"
                      value="subcontractor"
                      checked={shiftFormData.resourceType === 'subcontractor'}
                      onChange={(e) => setShiftFormData({
                        ...shiftFormData,
                        resourceType: e.target.value,
                        agentId: '',
                        subcontractorId: ''
                      })}
                      className="sr-only"
                    />
                    <Building2 className="w-5 h-5 mr-2 text-purple-600" />
                    <span className="font-medium">Sous-traitant</span>
                  </label>
                </div>
              </div>

              {/* Agent Selection */}
              {shiftFormData.resourceType === 'agent' && (
                <div className="space-y-2">
                  <Label htmlFor="agentId">Agent *</Label>
                  <select
                    id="agentId"
                    name="agentId"
                    value={shiftFormData.agentId}
                    onChange={handleShiftInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un agent</option>
                    {agents.map(agent => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name} - {agent.badge || agent.speciality}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Subcontractor Selection */}
              {shiftFormData.resourceType === 'subcontractor' && (
                <div className="space-y-2">
                  <Label htmlFor="subcontractorId">Sous-traitant *</Label>
                  <select
                    id="subcontractorId"
                    name="subcontractorId"
                    value={shiftFormData.subcontractorId}
                    onChange={handleShiftInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Sélectionner un sous-traitant</option>
                    {subcontractors.map(sub => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name} - {sub.companyName} ({sub.tarifHoraire}€/h)
                      </option>
                    ))}
                  </select>
                  {subcontractors.length === 0 && (
                    <p className="text-sm text-orange-600">
                      Aucun sous-traitant disponible. Ajoutez-en dans la section Partenaires.
                    </p>
                  )}
                </div>
              )}

              {/* Date and Shift Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={shiftFormData.date}
                    onChange={handleShiftInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shiftType">Type de garde</Label>
                  <select
                    id="shiftType"
                    name="shiftType"
                    value={shiftFormData.shiftType}
                    onChange={handleShiftInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="day">Jour (06h-18h)</option>
                    <option value="night">Nuit (18h-06h)</option>
                    <option value="morning">Matin (06h-14h)</option>
                    <option value="afternoon">Après-midi (14h-22h)</option>
                    <option value="custom">Personnalisé</option>
                  </select>
                </div>
              </div>

              {/* Horaires Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-4 flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Horaires de travail
                </h3>
                
                <div className="grid grid-cols-2 gap-6">
                  {/* Arrivée */}
                  <div className="space-y-2">
                    <Label htmlFor="startTime" className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      Heure d'arrivée *
                    </Label>
                    <Input
                      id="startTime"
                      name="startTime"
                      type="time"
                      value={shiftFormData.startTime}
                      onChange={handleShiftInputChange}
                      className="text-lg font-medium"
                      required
                    />
                  </div>

                  {/* Départ */}
                  <div className="space-y-2">
                    <Label htmlFor="endTime" className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                      Heure de départ *
                    </Label>
                    <Input
                      id="endTime"
                      name="endTime"
                      type="time"
                      value={shiftFormData.endTime}
                      onChange={handleShiftInputChange}
                      className="text-lg font-medium"
                      required
                    />
                  </div>

                  {/* Pause début */}
                  <div className="space-y-2">
                    <Label htmlFor="breakStart" className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                      Début repos
                    </Label>
                    <Input
                      id="breakStart"
                      name="breakStart"
                      type="time"
                      value={shiftFormData.breakStart}
                      onChange={handleShiftInputChange}
                      className="text-lg"
                    />
                  </div>

                  {/* Pause fin */}
                  <div className="space-y-2">
                    <Label htmlFor="breakEnd" className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                      Fin repos
                    </Label>
                    <Input
                      id="breakEnd"
                      name="breakEnd"
                      type="time"
                      value={shiftFormData.breakEnd}
                      onChange={handleShiftInputChange}
                      className="text-lg"
                    />
                  </div>
                </div>

                {/* Time calculation preview */}
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Temps de travail</div>
                      <div className="text-lg font-bold text-blue-600">
                        {(() => {
                          try {
                            const start = new Date(`2000-01-01T${shiftFormData.startTime}`);
                            const end = new Date(`2000-01-01T${shiftFormData.endTime}`);
                            const breakStart = new Date(`2000-01-01T${shiftFormData.breakStart}`);
                            const breakEnd = new Date(`2000-01-01T${shiftFormData.breakEnd}`);
                            const totalMs = end - start - (breakEnd - breakStart);
                            const hours = Math.floor(totalMs / (1000 * 60 * 60));
                            const mins = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
                            return `${hours}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}`;
                          } catch {
                            return '-';
                          }
                        })()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Temps de repos</div>
                      <div className="text-lg font-bold text-orange-600">
                        {(() => {
                          try {
                            const breakStart = new Date(`2000-01-01T${shiftFormData.breakStart}`);
                            const breakEnd = new Date(`2000-01-01T${shiftFormData.breakEnd}`);
                            const breakMs = breakEnd - breakStart;
                            const mins = Math.floor(breakMs / (1000 * 60));
                            return `${Math.floor(mins / 60)}h${mins % 60 > 0 ? (mins % 60).toString().padStart(2, '0') : ''}`;
                          } catch {
                            return '-';
                          }
                        })()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Durée totale</div>
                      <div className="text-lg font-bold text-gray-700">
                        {(() => {
                          try {
                            const start = new Date(`2000-01-01T${shiftFormData.startTime}`);
                            const end = new Date(`2000-01-01T${shiftFormData.endTime}`);
                            const totalMs = end - start;
                            const hours = Math.floor(totalMs / (1000 * 60 * 60));
                            return `${hours}h`;
                          } catch {
                            return '-';
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsAddShiftDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Ajout...' : 'Ajouter la garde'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="site-planning">Planning par Site</TabsTrigger>
          <TabsTrigger value="planning">Vue Semaine</TabsTrigger>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="shifts">Gardes</TabsTrigger>
        </TabsList>

        {/* NEW: Planning par Site Tab */}
        <TabsContent value="site-planning" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Planning par Site</CardTitle>
              <CardDescription>Gérez les affectations et horaires des agents par site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Site Selector */}
              <div className="space-y-2">
                <Label htmlFor="selectedSite">Selectionner un site</Label>
                <select
                  id="selectedSite"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedSiteForPlanning}
                  onChange={(e) => setSelectedSiteForPlanning(e.target.value)}
                >
                  <option value="">-- Choisir un site --</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>{site.name}{site.client ? ` - ${site.client}` : ''}{site.address ? ` (${site.address})` : ''}</option>
                  ))}
                </select>
              </div>

              {/* Add Shift Button */}
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Agents assignés</h3>
                <Button onClick={() => setIsAddShiftDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une garde
                </Button>
              </div>

              {/* Shifts Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Agent</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Heure d'arrivée</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Heure de repos</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Heure de départ</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Total heures</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {shifts.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>Aucun shift planifié pour aujourd'hui</p>
                          <p className="text-sm mt-1">Créez votre premier shift pour commencer</p>
                        </td>
                      </tr>
                    ) : (
                      shifts.map((shift) => (
                        <tr key={shift.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span>{shift.agentName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">{new Date(shift.date).toLocaleDateString('fr-FR')}</td>
                          <td className="px-4 py-3 font-medium text-green-600">{shift.startTime}</td>
                          <td className="px-4 py-3 text-orange-600">{shift.breakTime || '-'}</td>
                          <td className="px-4 py-3 font-medium text-red-600">{shift.endTime}</td>
                          <td className="px-4 py-3">
                            <Badge>{shift.totalHours}h</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Button variant="ghost" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">12</div>
                      <div className="text-sm text-gray-600">Agents sur site</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">96h</div>
                      <div className="text-sm text-gray-600">Total heures/semaine</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">8h</div>
                      <div className="text-sm text-gray-600">Heures supplémentaires</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">24/7</div>
                      <div className="text-sm text-gray-600">Couverture</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="planning" className="space-y-6">
          {/* Date Selector */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CalendarIcon className="w-5 h-5" />
                <span>Vue hebdomadaire</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-4 mb-6">
                <Label>Date de début:</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-48"
                />
              </div>
              
              {/* Weekly Planning Grid */}
              <div className="overflow-x-auto">
                <div className="grid grid-cols-8 gap-2 min-w-full">
                  {/* Header */}
                  <div className="font-medium text-center p-2">Agent</div>
                  {weekDates.map((date, index) => (
                    <div key={date} className="font-medium text-center p-2 bg-gray-50 rounded">
                      <div>{weekDays[index]}</div>
                      <div className="text-sm text-gray-600">
                        {new Date(date).getDate()}/{new Date(date).getMonth() + 1}
                      </div>
                    </div>
                  ))}
                  
                  {/* Agent Rows */}
                  {agents.filter(agent => agent.status === 'Actif').map((agent) => (
                    <React.Fragment key={agent.id}>
                      <div className="p-2 border-r flex items-center space-x-2">
                        <img 
                          src={agent.photo} 
                          alt={agent.name}
                          className="w-8 h-8 rounded-full"
                        />
                        <div>
                          <div className="font-medium text-sm">{agent.name}</div>
                          <div className="text-xs text-gray-500">{agent.speciality}</div>
                        </div>
                      </div>
                      
                      {weekDates.map((date) => {
                        const agentShifts = shifts.filter(shift => 
                          shift.agentId === agent.id && shift.date === date
                        );
                        
                        return (
                          <div key={`${agent.id}-${date}`} className="p-1 border border-gray-200 min-h-16">
                            {agentShifts.map((shift) => (
                              <div
                                key={shift.id}
                                className={`text-xs p-2 rounded mb-1 border ${getShiftColor(shift.type)}`}
                              >
                                <div className="font-medium">{shift.type}</div>
                                <div>{shift.startTime}-{shift.endTime}</div>
                                <div className="truncate">{shift.siteName}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="space-y-6">
          {/* Search and Filters */}
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Rechercher un agent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="actif">Actif</SelectItem>
                <SelectItem value="repos">Repos</SelectItem>
                <SelectItem value="en congé">En congé</SelectItem>
                <SelectItem value="indisponible">Indisponible</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Agents Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <Card key={agent.id} className="hover:shadow-lg transition-shadow duration-200">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <img 
                        src={agent.photo} 
                        alt={agent.name}
                        className="w-12 h-12 rounded-full"
                      />
                      <div>
                        <CardTitle className="text-lg">{agent.name}</CardTitle>
                        <CardDescription>{agent.badge}</CardDescription>
                        <Badge variant="outline" className={`mt-1 ${getLevelColor(agent.level)}`}>
                          {agent.level}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(agent.status)}`}></div>
                      <Badge variant={getStatusVariant(agent.status)}>{agent.status}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span>{agent.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span>{agent.email}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Award className="w-4 h-4 text-gray-500" />
                      <span>{agent.speciality}</span>
                    </div>
                    {agent.currentSite && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span>{agent.currentSite}</span>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-xs font-medium text-gray-500">CERTIFICATIONS</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {agent.certifications.map((cert, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex space-x-2 pt-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setSelectedAgent(agent)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Voir
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1">
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="shifts" className="space-y-6">
          {/* Today's Shifts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="w-5 h-5" />
                <span>Gardes du jour - {new Date(selectedDate).toLocaleDateString('fr-FR')}</span>
              </CardTitle>
              <CardDescription>
                {todayShifts.length} garde(s) programmée(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {todayShifts.map((shift) => (
                  <div key={shift.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className={`w-4 h-4 rounded-full ${getShiftColor(shift.type).split(' ')[0]}`}></div>
                      <div>
                        <div className="flex items-center space-x-3">
                          <h3 className="font-semibold">{shift.agentName}</h3>
                          <Badge className={getShiftColor(shift.type)}>
                            {shift.type}
                          </Badge>
                          <Badge variant="outline" className={getShiftStatusColor(shift.status)}>
                            {shift.status}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{shift.startTime} - {shift.endTime}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-3 h-3" />
                            <span>{shift.siteName}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button size="sm" variant="outline">
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                    </div>
                  </div>
                ))}
                
                {todayShifts.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Aucune garde programmée pour cette date</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold">{agents.filter(a => a.status === 'Actif').length}</div>
                    <div className="text-sm text-gray-600">Agents actifs</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold">{shifts.filter(s => s.status === 'En cours').length}</div>
                    <div className="text-sm text-gray-600">Gardes en cours</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <CalendarIcon className="w-5 h-5 text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold">{shifts.filter(s => s.status === 'Programmé').length}</div>
                    <div className="text-sm text-gray-600">Gardes programmées</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <User className="w-5 h-5 text-purple-600" />
                  <div>
                    <div className="text-2xl font-bold">{agents.filter(a => a.status === 'En congé').length}</div>
                    <div className="text-sm text-gray-600">En congé</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Agent Detail Modal */}
      {selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <img 
                    src={selectedAgent.photo} 
                    alt={selectedAgent.name}
                    className="w-16 h-16 rounded-full"
                  />
                  <div>
                    <CardTitle>{selectedAgent.name}</CardTitle>
                    <CardDescription>{selectedAgent.badge} • {selectedAgent.speciality}</CardDescription>
                    <Badge variant={getStatusVariant(selectedAgent.status)} className="mt-2">
                      {selectedAgent.status}
                    </Badge>
                  </div>
                </div>
                <Button variant="ghost" onClick={() => setSelectedAgent(null)}>
                  ×
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">Informations personnelles</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">Téléphone:</span>
                      <span>{selectedAgent.phone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Email:</span>
                      <span>{selectedAgent.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Date d'embauche:</span>
                      <span>{new Date(selectedAgent.hireDate).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Niveau:</span>
                      <Badge className={getLevelColor(selectedAgent.level)}>
                        {selectedAgent.level}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold">Affectation actuelle</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">Site:</span>
                      <span>{selectedAgent.currentSite || 'Non assigné'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Spécialité:</span>
                      <span>{selectedAgent.speciality}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3">Certifications</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedAgent.certifications.map((cert, index) => (
                    <Badge key={index} variant="outline">
                      {cert}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3">Prochaines gardes</h3>
                <div className="space-y-2">
                  {shifts
                    .filter(shift => shift.agentId === selectedAgent.id && shift.status === 'Programmé')
                    .slice(0, 3)
                    .map((shift, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{new Date(shift.date).toLocaleDateString('fr-FR')}</span>
                          <span className="ml-2 text-sm text-gray-600">
                            {shift.startTime} - {shift.endTime}
                          </span>
                        </div>
                        <div className="text-sm">
                          <Badge className={getShiftColor(shift.type)}>
                            {shift.type}
                          </Badge>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Planning;